import React, { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  User,
  Mail,
  Phone,
  Shield,
  Bell,
  Globe,
  Eye,
  EyeOff,
  Camera,
  Save,
  Lock,
  Smartphone,
  CreditCard,
  Download,
  Trash2,
  AlertTriangle,
  Check,
  Loader,
} from "lucide-react";
import { useAuth } from "../hooks/useAuth";
import Toast from "../components/common/Toast";

const PROFILE_API_CANDIDATES = [
  import.meta.env.VITE_BACKEND_API_BASE_URL,
  import.meta.env.VITE_AUTH_API_BASE_URL,
  "https://gainwisefx.com/api/",
  "/api",
].filter((value, index, list) => value && list.indexOf(value) === index);

const getProfileAuthHeaders = () => {
  const token =
    localStorage.getItem("authToken") ||
    localStorage.getItem("token") ||
    localStorage.getItem("trading_app_token") ||
    sessionStorage.getItem("authToken") ||
    sessionStorage.getItem("token") ||
    sessionStorage.getItem("trading_app_token") ||
    "";

  const headers = { "Content-Type": "application/json" };

  if (token) {
    headers.Authorization = `Bearer ${token}`;
    headers["X-Auth-Token"] = token;
  }

  return headers;
};

const panelClass =
  "rounded-2xl border border-slate-800 bg-slate-950/85 p-6 shadow-sm shadow-slate-950/20";
const sectionTitleClass = "mb-6 text-lg font-semibold text-slate-100";
const subTitleClass = "font-medium text-slate-100";
const bodyMutedClass = "text-sm text-slate-400";
const labelClass = "mb-2 block text-sm font-medium text-slate-300";
const inputClass =
  "w-full rounded-lg border border-slate-700 bg-slate-900 px-4 py-3 text-slate-100 placeholder:text-slate-500 focus:border-cyan-400 focus:ring-2 focus:ring-cyan-400/30 focus:outline-none";
const iconFieldClass =
  "w-full rounded-lg border border-slate-700 bg-slate-900 py-3 pl-10 pr-4 text-slate-100 placeholder:text-slate-500 focus:border-cyan-400 focus:ring-2 focus:ring-cyan-400/30 focus:outline-none";
const toggleClass =
  "h-6 w-11 rounded-full bg-slate-700 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-cyan-400/20 peer peer-checked:after:translate-x-full peer-checked:after:border-white after:absolute after:left-[2px] after:top-[2px] after:h-5 after:w-5 after:rounded-full after:border after:border-slate-400 after:bg-white after:transition-all peer-checked:bg-cyan-500";
const dividerClass = "border-t border-slate-800 pt-6";
const primaryButtonClass =
  "flex items-center space-x-2 rounded-lg bg-cyan-500 px-6 py-3 font-medium text-slate-950 transition-colors hover:bg-cyan-400 disabled:bg-cyan-500/60";

const Profile = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("profile");
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isDataLoading, setIsDataLoading] = useState(true);
  const [successMessage, setSuccessMessage] = useState("");
  const [toast, setToast] = useState(null);
  const fileInputRef = useRef(null);
  const { user } = useAuth();

  // Profile form state
  const [profileData, setProfileData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    country: "",
    timezone: "America/New_York",
    avatar: null,
  });

  // User details from API
  const [userDetails, setUserDetails] = useState(null);

  // Security form state
  const [securityData, setSecurityData] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
    twoFactorEnabled: true,
    loginNotifications: true,
  });

  // Notification preferences
  const [notificationSettings, setNotificationSettings] = useState({
    emailNotifications: true,
    pushNotifications: true,
    smsNotifications: false,
    tradingAlerts: true,
    priceAlerts: true,
    newsAlerts: false,
    marketingEmails: false,
  });

  // App preferences
  const [appPreferences, setAppPreferences] = useState({
    theme: "light",
    language: "en",
    defaultCurrency: "USD",
    chartType: "candlestick",
    portfolioVisibility: false,
    tradingActivityVisible: true,
  });

  const tabs = [
    { id: "profile", label: "Profile", icon: User },
    { id: "security", label: "Security", icon: Shield },
    { id: "notifications", label: "Notifications", icon: Bell },
    { id: "preferences", label: "Preferences", icon: Globe },
  ];

  const handleBackNavigation = () => {
    if (window.history.length > 1) {
      navigate(-1);
      return;
    }

    navigate("/dashboard", { replace: true });
  };

  // Fetch user details on component mount
  useEffect(() => {
    fetchUserDetails();
  }, []);

  const fetchUserDetails = async () => {
    try {
      setIsDataLoading(true);
      let result = null;

      for (const baseUrl of PROFILE_API_CANDIDATES) {
        try {
          const response = await fetch(`${baseUrl}/get-user-details.php`, {
            method: "GET",
            headers: getProfileAuthHeaders(),
          });

          if (!response.ok) {
            continue;
          }

          const payload = await response.json();
          if (payload?.success) {
            result = payload;
            break;
          }
        } catch {
          continue;
        }
      }

      if (!result?.success) throw new Error("Failed to fetch user details");

      if (result.success) {
        const userData = result.user;
        const preferences = result.preferences;

        // Update profile data
        setProfileData({
          firstName: userData.firstName || "",
          lastName: userData.lastName || "",
          email: userData.email || "",
          phone: userData.phone || "",
          country: userData.country || "",
          timezone: userData.timezone || "America/New_York",
          avatar: userData.avatar,
        });

        // Update user details
        setUserDetails(userData);

        // Update security data
        setSecurityData((prev) => ({
          ...prev,
          twoFactorEnabled: userData.twoFactorEnabled,
          loginNotifications: userData.loginNotificationsEnabled,
        }));

        // Update notification settings
        setNotificationSettings(preferences.notifications);

        // Update app preferences
        setAppPreferences({
          theme: preferences.theme,
          language: preferences.language,
          defaultCurrency: preferences.defaultCurrency,
          chartType: preferences.chartType,
          portfolioVisibility: preferences.privacy.portfolioVisibility,
          tradingActivityVisible: preferences.privacy.tradingActivityVisible,
        });
      } else {
        setToast({
          type: "error",
          message: result.message || "Failed to load user details",
        });
      }
    } catch (error) {
      console.error("Error fetching user details:", error);
      setToast({
        type: "error",
        message: "Unable to load user details. Please try again.",
      });
    } finally {
      setIsDataLoading(false);
    }
  };

  const handleProfileUpdate = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      let result = null;

      for (const baseUrl of PROFILE_API_CANDIDATES) {
        try {
          const response = await fetch(`${baseUrl}/update-profile.php`, {
            method: "POST",
          headers: getProfileAuthHeaders(),
            body: JSON.stringify(profileData),
          });

          const payload = await response.json();
          if (response.ok && payload?.success) {
            result = payload;
            break;
          }

          result = payload;
        } catch {
          continue;
        }
      }

      if (!result) {
        throw new Error("Failed to update profile");
      }

      if (result.success) {
        setSuccessMessage("Profile updated successfully!");
        setTimeout(() => setSuccessMessage(""), 3000);
        // Refresh user details
        fetchUserDetails();
      } else {
        setToast({
          type: "error",
          message: result.message || "Failed to update profile",
        });
      }
    } catch (error) {
      console.error("Error updating profile:", error);
      setToast({
        type: "error",
        message: "Unable to update profile. Please try again.",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSecurityUpdate = async (e) => {
    e.preventDefault();
    if (securityData.newPassword !== securityData.confirmPassword) {
      alert("Passwords do not match!");
      return;
    }

    setIsLoading(true);
    setTimeout(() => {
      setIsLoading(false);
      setSuccessMessage("Security settings updated successfully!");
      setSecurityData({
        ...securityData,
        currentPassword: "",
        newPassword: "",
        confirmPassword: "",
      });
      setTimeout(() => setSuccessMessage(""), 3000);
    }, 1000);
  };

  const handleAvatarChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        setProfileData({
          ...profileData,
          avatar: e.target.result,
        });
      };
      reader.readAsDataURL(file);
    }
  };

  const handleNotificationChange = (key, value) => {
    setNotificationSettings({
      ...notificationSettings,
      [key]: value,
    });
  };

  const renderProfileTab = () => (
    <div className="space-y-6">
      <div className={panelClass}>
        <h3 className={sectionTitleClass}>Personal Information</h3>

        {/* Avatar Upload */}
        <div className="flex items-center space-x-6 mb-6">
          <div className="relative">
            <div className="w-20 h-20 rounded-full overflow-hidden bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center">
              {profileData.avatar ? (
                <img
                  src={profileData.avatar}
                  alt="Avatar"
                  className="w-full h-full object-cover"
                />
              ) : (
                <User className="h-10 w-10 text-white" />
              )}
            </div>
            <button
              onClick={() => fileInputRef.current?.click()}
              className="absolute -bottom-1 -right-1 p-1.5 bg-blue-600 hover:bg-blue-700 rounded-full text-white transition-colors"
            >
              <Camera className="h-3 w-3" />
            </button>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handleAvatarChange}
              className="hidden"
            />
          </div>
          <div>
            <h4 className={subTitleClass}>Profile Picture</h4>
            <p className={bodyMutedClass}>
              JPG, PNG or GIF. Max size 2MB.
            </p>
          </div>
        </div>

        <form onSubmit={handleProfileUpdate} className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className={labelClass}>First Name</label>
              <input
                type="text"
                value={profileData.firstName}
                onChange={(e) =>
                  setProfileData({ ...profileData, firstName: e.target.value })
                }
                className={inputClass}
              />
            </div>
            <div>
              <label className={labelClass}>Last Name</label>
              <input
                type="text"
                value={profileData.lastName}
                onChange={(e) =>
                  setProfileData({ ...profileData, lastName: e.target.value })
                }
                className={inputClass}
              />
            </div>
          </div>

          <div>
            <label className={labelClass}>Email Address</label>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-slate-500" />
              <input
                type="email"
                value={profileData.email}
                onChange={(e) =>
                  setProfileData({ ...profileData, email: e.target.value })
                }
                className={iconFieldClass}
              />
            </div>
          </div>

          <div>
            <label className={labelClass}>Phone Number</label>
            <div className="relative">
              <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-slate-500" />
              <input
                type="tel"
                value={profileData.phone}
                onChange={(e) =>
                  setProfileData({ ...profileData, phone: e.target.value })
                }
                className={iconFieldClass}
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className={labelClass}>Country</label>
              <select
                value={profileData.country}
                onChange={(e) =>
                  setProfileData({ ...profileData, country: e.target.value })
                }
                className={inputClass}
              >
                <option>United States</option>
                <option>Canada</option>
                <option>United Kingdom</option>
                <option>Germany</option>
                <option>France</option>
                <option>Australia</option>
                <option>Japan</option>
              </select>
            </div>
            <div>
              <label className={labelClass}>Timezone</label>
              <select
                value={profileData.timezone}
                onChange={(e) =>
                  setProfileData({ ...profileData, timezone: e.target.value })
                }
                className={inputClass}
              >
                <option value="America/New_York">Eastern Time (ET)</option>
                <option value="America/Chicago">Central Time (CT)</option>
                <option value="America/Denver">Mountain Time (MT)</option>
                <option value="America/Los_Angeles">Pacific Time (PT)</option>
                <option value="Europe/London">London (GMT)</option>
                <option value="Europe/Paris">Paris (CET)</option>
                <option value="Asia/Tokyo">Tokyo (JST)</option>
              </select>
            </div>
          </div>

          <div className="flex justify-end">
            <button
              type="submit"
              disabled={isLoading}
              className={primaryButtonClass}
            >
              <Save className="h-4 w-4" />
              <span>{isLoading ? "Saving..." : "Save Changes"}</span>
            </button>
          </div>
        </form>
      </div>
    </div>
  );

  const renderSecurityTab = () => (
    <div className="space-y-6">
      {/* Change Password */}
      <div className={panelClass}>
        <h3 className={sectionTitleClass}>Change Password</h3>
        <form onSubmit={handleSecurityUpdate} className="space-y-6">
          <div>
            <label className={labelClass}>Current Password</label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-slate-500" />
              <input
                type={showCurrentPassword ? "text" : "password"}
                value={securityData.currentPassword}
                onChange={(e) =>
                  setSecurityData({
                    ...securityData,
                    currentPassword: e.target.value,
                  })
                }
                className={iconFieldClass}
              />
              <button
                type="button"
                onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-slate-500 hover:text-slate-300"
              >
                {showCurrentPassword ? (
                  <EyeOff className="h-5 w-5" />
                ) : (
                  <Eye className="h-5 w-5" />
                )}
              </button>
            </div>
          </div>

          <div>
            <label className={labelClass}>New Password</label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-slate-500" />
              <input
                type={showNewPassword ? "text" : "password"}
                value={securityData.newPassword}
                onChange={(e) =>
                  setSecurityData({
                    ...securityData,
                    newPassword: e.target.value,
                  })
                }
                className={iconFieldClass}
              />
              <button
                type="button"
                onClick={() => setShowNewPassword(!showNewPassword)}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-slate-500 hover:text-slate-300"
              >
                {showNewPassword ? (
                  <EyeOff className="h-5 w-5" />
                ) : (
                  <Eye className="h-5 w-5" />
                )}
              </button>
            </div>
          </div>

          <div>
            <label className={labelClass}>Confirm New Password</label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-slate-500" />
              <input
                type={showConfirmPassword ? "text" : "password"}
                value={securityData.confirmPassword}
                onChange={(e) =>
                  setSecurityData({
                    ...securityData,
                    confirmPassword: e.target.value,
                  })
                }
                className={iconFieldClass}
              />
              <button
                type="button"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-slate-500 hover:text-slate-300"
              >
                {showConfirmPassword ? (
                  <EyeOff className="h-5 w-5" />
                ) : (
                  <Eye className="h-5 w-5" />
                )}
              </button>
            </div>
          </div>

          <div className="flex justify-end">
            <button
              type="submit"
              disabled={isLoading}
              className={primaryButtonClass}
            >
              <Save className="h-4 w-4" />
              <span>{isLoading ? "Updating..." : "Update Password"}</span>
            </button>
          </div>
        </form>
      </div>

      {/* Two-Factor Authentication */}
      <div className={panelClass}>
        <h3 className={sectionTitleClass}>Two-Factor Authentication</h3>
        <div className="flex items-center justify-between p-4 bg-green-50 border border-green-200 rounded-lg">
          <div className="flex items-center space-x-3">
            <div className="flex-shrink-0">
              <Smartphone className="h-6 w-6 text-green-600" />
            </div>
            <div>
              <h4 className="font-medium text-green-900">2FA is enabled</h4>
              <p className="text-sm text-green-700">
                Your account is protected with two-factor authentication
              </p>
            </div>
          </div>
          <div className="flex items-center space-x-2">
            <Check className="h-5 w-5 text-green-600" />
            <button className="text-sm text-green-700 hover:text-green-800 font-medium">
              Manage
            </button>
          </div>
        </div>
      </div>

      {/* Login Notifications */}
      <div className={panelClass}>
        <h3 className={sectionTitleClass}>Security Notifications</h3>
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <h4 className={subTitleClass}>Login Notifications</h4>
              <p className={bodyMutedClass}>
                Get notified when someone logs into your account
              </p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={securityData.loginNotifications}
                onChange={(e) =>
                  setSecurityData({
                    ...securityData,
                    loginNotifications: e.target.checked,
                  })
                }
                className="sr-only peer"
              />
              <div className={toggleClass}></div>
            </label>
          </div>
        </div>
      </div>

      {/* Account Security */}
      <div className={panelClass}>
        <h3 className={sectionTitleClass}>Account Security</h3>
        <div className="space-y-4">
          <div className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
            <div className="flex items-center space-x-3">
              <Download className="h-5 w-5 text-gray-400" />
              <div>
                <h4 className={subTitleClass}>
                  Download Account Data
                </h4>
                <p className={bodyMutedClass}>
                  Download a copy of your account data
                </p>
              </div>
            </div>
            <button className="text-blue-600 hover:text-blue-700 font-medium text-sm">
              Download
            </button>
          </div>

          <div className="flex items-center justify-between p-4 border border-red-200 rounded-lg bg-red-50">
            <div className="flex items-center space-x-3">
              <Trash2 className="h-5 w-5 text-red-500" />
              <div>
                <h4 className="font-medium text-red-900">Delete Account</h4>
                <p className="text-sm text-red-700">
                  Permanently delete your account and all data
                </p>
              </div>
            </div>
            <button className="text-red-600 hover:text-red-700 font-medium text-sm">
              Delete
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  const renderNotificationsTab = () => (
    <div className="space-y-6">
      <div className={panelClass}>
        <h3 className={sectionTitleClass}>Notification Preferences</h3>

        <div className="space-y-6">
          {/* Email Notifications */}
          <div>
            <h4 className="mb-4 font-medium text-slate-100">
              Email Notifications
            </h4>
            <div className="space-y-4">
              {[
                {
                  key: "emailNotifications",
                  label: "Email Notifications",
                  desc: "Receive notifications via email",
                },
                {
                  key: "tradingAlerts",
                  label: "Trading Alerts",
                  desc: "Get notified about your trades and orders",
                },
                {
                  key: "priceAlerts",
                  label: "Price Alerts",
                  desc: "Receive alerts when prices reach your targets",
                },
                {
                  key: "newsAlerts",
                  label: "News & Updates",
                  desc: "Stay updated with market news",
                },
                {
                  key: "marketingEmails",
                  label: "Marketing Emails",
                  desc: "Receive promotional content and offers",
                },
              ].map(({ key, label, desc }) => (
                <div key={key} className="flex items-center justify-between">
                  <div>
                    <h5 className="font-medium text-slate-100">{label}</h5>
                    <p className={bodyMutedClass}>{desc}</p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={notificationSettings[key]}
                      onChange={(e) =>
                        handleNotificationChange(key, e.target.checked)
                      }
                      className="sr-only peer"
                    />
                    <div className={toggleClass}></div>
                  </label>
                </div>
              ))}
            </div>
          </div>

          {/* Push Notifications */}
          <div className={dividerClass}>
            <h4 className="mb-4 font-medium text-slate-100">
              Push Notifications
            </h4>
            <div className="space-y-4">
              {[
                {
                  key: "pushNotifications",
                  label: "Push Notifications",
                  desc: "Receive push notifications on your device",
                },
                {
                  key: "smsNotifications",
                  label: "SMS Notifications",
                  desc: "Receive notifications via SMS",
                },
              ].map(({ key, label, desc }) => (
                <div key={key} className="flex items-center justify-between">
                  <div>
                    <h5 className="font-medium text-slate-100">{label}</h5>
                    <p className={bodyMutedClass}>{desc}</p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={notificationSettings[key]}
                      onChange={(e) =>
                        handleNotificationChange(key, e.target.checked)
                      }
                      className="sr-only peer"
                    />
                    <div className={toggleClass}></div>
                  </label>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="mt-6 flex justify-end border-t border-slate-800 pt-6">
          <button
            onClick={() => {
              setSuccessMessage("Notification preferences saved!");
              setTimeout(() => setSuccessMessage(""), 3000);
            }}
            className={primaryButtonClass}
          >
            <Save className="h-4 w-4" />
            <span>Save Preferences</span>
          </button>
        </div>
      </div>
    </div>
  );

  const renderPreferencesTab = () => (
    <div className="space-y-6">
      <div className={panelClass}>
        <h3 className={sectionTitleClass}>App Preferences</h3>

        <div className="space-y-6">
          <div>
            <h4 className="mb-4 font-medium text-slate-100">Display Settings</h4>
            <div className="space-y-4">
              <div>
                <label className={labelClass}>Theme</label>
                <select className={inputClass}>
                  <option>Light</option>
                  <option>Dark</option>
                  <option>Auto</option>
                </select>
              </div>
              <div>
                <label className={labelClass}>Language</label>
                <select className={inputClass}>
                  <option>English</option>
                  <option>Spanish</option>
                  <option>French</option>
                  <option>German</option>
                  <option>Chinese</option>
                  <option>Japanese</option>
                </select>
              </div>
            </div>
          </div>

          <div className={dividerClass}>
            <h4 className="mb-4 font-medium text-slate-100">
              Trading Preferences
            </h4>
            <div className="space-y-4">
              <div>
                <label className={labelClass}>Default Currency</label>
                <select className={inputClass}>
                  <option>USD</option>
                  <option>EUR</option>
                  <option>GBP</option>
                  <option>JPY</option>
                  <option>BTC</option>
                </select>
              </div>
              <div>
                <label className={labelClass}>Chart Type</label>
                <select className={inputClass}>
                  <option>Candlestick</option>
                  <option>Line Chart</option>
                  <option>Area Chart</option>
                  <option>Bar Chart</option>
                </select>
              </div>
            </div>
          </div>

          <div className={dividerClass}>
            <h4 className="mb-4 font-medium text-slate-100">Privacy Settings</h4>
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <h5 className="font-medium text-slate-100">
                    Portfolio Visibility
                  </h5>
                  <p className={bodyMutedClass}>
                    Allow others to see your portfolio performance
                  </p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input type="checkbox" className="sr-only peer" />
                  <div className={toggleClass}></div>
                </label>
              </div>
              <div className="flex items-center justify-between">
                <div>
                  <h5 className="font-medium text-slate-100">
                    Trading Activity
                  </h5>
                  <p className={bodyMutedClass}>
                    Show your trading activity to followers
                  </p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    defaultChecked
                    className="sr-only peer"
                  />
                  <div className={toggleClass}></div>
                </label>
              </div>
            </div>
          </div>
        </div>

        <div className="mt-6 flex justify-end border-t border-slate-800 pt-6">
          <button
            onClick={() => {
              setSuccessMessage("Preferences saved successfully!");
              setTimeout(() => setSuccessMessage(""), 3000);
            }}
            className={primaryButtonClass}
          >
            <Save className="h-4 w-4" />
            <span>Save Preferences</span>
          </button>
        </div>
      </div>
    </div>
  );

  if (isDataLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[radial-gradient(circle_at_top_left,_rgba(8,145,178,0.18),_transparent_24%),linear-gradient(180deg,#020617,#0f172a)]">
        <div className="text-center">
          <Loader className="mx-auto mb-4 h-8 w-8 animate-spin text-cyan-400" />
          <p className="text-slate-300">Loading your profile...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[radial-gradient(circle_at_top_left,_rgba(8,145,178,0.14),_transparent_24%),linear-gradient(180deg,#020617,#0f172a)] py-8 text-slate-100">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <button
            type="button"
            onClick={handleBackNavigation}
            className="mb-4 inline-flex items-center gap-2 rounded-xl border border-slate-700 bg-slate-900/80 px-4 py-2 text-sm font-medium text-slate-200 transition hover:border-cyan-400 hover:text-cyan-300 hover:shadow-lg hover:shadow-cyan-950/20"
          >
            <ArrowLeft className="h-4 w-4" />
            Back
          </button>
          <h1 className="text-3xl font-bold text-slate-100">Account Settings</h1>
          <p className="mt-2 text-slate-400">
            Manage your account preferences and security settings
          </p>
        </div>

        {/* Success Message */}
        {successMessage && (
          <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg flex items-center space-x-3">
            <Check className="h-5 w-5 text-green-600" />
            <span className="text-green-800 font-medium">{successMessage}</span>
          </div>
        )}

        {/* Navigation Tabs */}
        <div className="mb-6 rounded-2xl border border-slate-800 bg-slate-950/85 shadow-sm shadow-slate-950/20">
          <div className="border-b border-slate-800">
            <nav className="flex space-x-8 px-6">
              {tabs.map((tab) => {
                const Icon = tab.icon;
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`flex items-center space-x-2 py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                      activeTab === tab.id
                        ? "border-cyan-400 text-cyan-300"
                        : "border-transparent text-slate-400 hover:text-slate-200 hover:border-slate-600"
                    }`}
                  >
                    <Icon className="h-5 w-5" />
                    <span>{tab.label}</span>
                  </button>
                );
              })}
            </nav>
          </div>
        </div>

        {/* Tab Content */}
        <div className="tab-content">
          {activeTab === "profile" && renderProfileTab()}
          {activeTab === "security" && renderSecurityTab()}
          {activeTab === "notifications" && renderNotificationsTab()}
          {activeTab === "preferences" && renderPreferencesTab()}
        </div>

        {/* Toast Notifications */}
        {toast && (
          <Toast
            type={toast.type}
            message={toast.message}
            onClose={() => setToast(null)}
          />
        )}
      </div>
    </div>
  );
};

export default Profile;
